import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import { useState } from 'react';
import { type ContactRow, listContacts, mergeContacts } from './api';

interface Props {
  source: ContactRow;
  onClose: () => void;
}

export function MergeModal({ source, onClose }: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<ContactRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const searchQ = useQuery({
    queryKey: ['contacts-search-merge', query],
    queryFn: () => listContacts({ q: query, limit: 10 }),
    enabled: query.length >= 2,
  });

  const mut = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('select target');
      return mergeContacts({
        source_contact_id: source.id,
        target_contact_id: target.id,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.title : 'Falha ao mesclar');
    },
  });

  const candidates = (searchQ.data?.contacts ?? []).filter((c) => c.id !== source.id);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[520px] max-w-full rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-[16px] text-n-900">Mesclar contatos</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 rounded-md border border-n-200 bg-n-50 p-3 text-[12px]">
          <div className="font-semibold text-n-500">Origem (será apagado)</div>
          <div className="mt-1 font-bold text-n-900">{source.display_name}</div>
          <div className="font-mono text-n-600">{source.primary_identifier?.value ?? '—'}</div>
        </div>

        <div className="mt-4">
          <div className="font-semibold text-[12px] text-n-700">
            Buscar contato destino (recebe o histórico):
          </div>
          <div className="mt-2 flex h-[34px] items-center gap-2 rounded-pill border border-n-200 bg-n-0 px-3 text-[13px]">
            <Search size={14} className="text-n-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Digite nome ou número…"
              autoFocus
              className="flex-1 bg-transparent outline-none"
            />
          </div>

          {query.length >= 2 && (
            <div className="mt-2 max-h-[180px] overflow-y-auto rounded-md border border-n-200">
              {searchQ.isLoading && (
                <div className="px-3 py-2 text-[12px] text-n-500">Buscando…</div>
              )}
              {!searchQ.isLoading && candidates.length === 0 && (
                <div className="px-3 py-2 text-[12px] text-n-500">Nenhum contato encontrado.</div>
              )}
              {candidates.map((c) => {
                const selected = target?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setTarget(c)}
                    className={`flex w-full items-center justify-between border-b border-n-100 px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-brand-50 ${
                      selected ? 'bg-brand-50' : ''
                    }`}
                  >
                    <span className="font-medium text-n-900">{c.display_name}</span>
                    <span className="font-mono text-[11px] text-n-500">
                      {c.primary_identifier?.value ?? '—'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {target && (
          <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 p-3 text-[12px]">
            <div className="font-semibold text-brand-700">Destino selecionado</div>
            <div className="mt-1 font-bold text-n-900">{target.display_name}</div>
            <div className="font-mono text-n-600">{target.primary_identifier?.value ?? '—'}</div>
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={!target || mut.isPending}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Mesclar
          </Button>
        </div>
      </div>
    </div>
  );
}
